/**
 * EntityRenderer: pooled meshes per mob type + arrows, driven by MobView /
 * ArrowView lists. Vanilla geometry and animation, light-modulated, with
 * hurt / creeper-flash / death / fire effects.
 *
 *   const r = new EntityRenderer(env); scene.add(r.group);
 *   each frame: r.setViewer(cam.x, cam.y, cam.z); r.update(mobViews, arrowViews, alpha);
 */

import * as THREE from 'three';
import type { ArrowView, MobType, MobView } from '../../game/types';
import type { EnvUniforms } from '../env';
import { modelRoot, setEffects, wrapPi, type EntityMat } from './core';

const LIGHT_AT = { x: 0, y: 0, z: 0 };
import { arrowMesh, applyHandheld, FireOverlay, itemSpriteMesh } from './extras';
import { Humanoid } from './humanoid';
import { blazeAnim, endermanAnim, ghastAnim, hoglinAnim, magmaCubeAnim, piglinAnim, striderAnim, witherSkeletonAnim } from './netherModels';
import { ironGolemAnim, illagerAnim, villagerAnim } from './villageModels';
import { caveSpiderAnim, drownedAnim, endermiteAnim, huskAnim, silverfishAnim, slimeAnim, strayAnim, wolfAnim } from './overworldModels';
import { batAnim, catAnim, fishAnim, squidAnim, phantomAnim, witchAnim } from './overworldModels2';
import { horseAnim } from './horseModel';
import { foxAnim, pandaAnim, parrotAnim } from './jungleModels';
import { llamaAnim, wanderingTraderAnim } from './traderModels';
import { babyAnim, hasBabyModel } from './babyModels';
import { witherAnim } from './witherModel';
import { axolotlAnim, dolphinAnim, evokerAnim, evokerFangsAnim, goatAnim, guardianAnim, mooshroomAnim, ocelotAnim, polarBearAnim, rabbitAnim, ravagerAnim, shulkerAnim, shulkerBulletAnim, snowGolemAnim, turtleAnim, vexAnim } from './mobs3Models';
import { MOBS3_SIZE } from '../../entities/mobs3';
import { wardenAnim } from './wardenModel';
import { ChickenModel, CowModel, CreeperModel, PigModel, SheepModel, SpiderModel, type Model, type QuadPose } from './mobs';

const PI = Math.PI;
/** hitbox width/height per type (fire overlay size). */
export const SIZE: Record<MobType, [number, number]> = {
  zombie: [0.6, 1.95], skeleton: [0.6, 1.99], creeper: [0.6, 1.7], spider: [1.4, 0.9],
  pig: [0.9, 0.9], cow: [0.9, 1.4], sheep: [0.9, 1.3], chicken: [0.4, 0.7],
  zombified_piglin: [0.6, 1.95], piglin: [0.6, 1.95], magma_cube: [1.04, 1.04],
  wither: [0.9, 3.5], ghast: [4, 4], blaze: [0.6, 1.8], wither_skeleton: [0.7, 2.4], hoglin: [1.4, 1.4], strider: [0.9, 1.7], enderman: [0.6, 2.9],
  villager: [0.6, 1.95], iron_golem: [1.4, 2.7], pillager: [0.6, 1.95], vindicator: [0.6, 1.95], zombie_villager: [0.6, 1.95],
  slime: [1.04, 1.04], cave_spider: [0.7, 0.5], husk: [0.6, 1.95], stray: [0.6, 1.99], drowned: [0.6, 1.95],
  horse: [1.4, 1.6], donkey: [1.4, 1.5], silverfish: [0.4, 0.3], endermite: [0.4, 0.3], wolf: [0.6, 0.85], witch: [0.6, 1.95], phantom: [0.9, 0.5], cat: [0.6, 0.7], squid: [0.95, 0.95], cod: [0.5, 0.3], salmon: [0.7, 0.4], bat: [0.5, 0.9],
  panda: [1.3, 1.25], parrot: [0.5, 0.9], fox: [0.6, 0.7], warden: [0.9, 2.9],
  wandering_trader: [0.6, 1.95], llama: [0.9, 1.87], trader_llama: [0.9, 1.87],
  ...MOBS3_SIZE,
};
/** Every MobType with a model (Record keys: tsc forces new mobs in). */
export const MOB_TYPES = Object.keys(SIZE) as MobType[];
const CULL_DIST = 96;

interface Anim {
  parts: THREE.Object3D;
  mats: EntityMat[];
  /** Head part, scaled up for babies (optional). */
  head?: THREE.Object3D;
  animate(v: MobView, q: QuadPose, headY: number, headX: number, age: number): void;
  dispose(): void;
}

function humanoidAnim(env: EnvUniforms, kind: 'zombie' | 'skeleton'): Anim {
  const h = kind === 'zombie'
    ? new Humanoid(env, { sheet: 'entity/zombie/zombie', modern: false, tw: 64, th: 64 })
    : new Humanoid(env, { sheet: 'entity/skeleton/skeleton', modern: false, thin: true, tw: 64, th: 32 });
  let bow: THREE.Object3D | null = null;
  let bowMat: EntityMat | null = null;
  return {
    parts: h.root, mats: h.mats,
    animate(v, q, headY, headX, age) {
      const hasBow = kind === 'skeleton' && v.heldItem !== 0;
      if (hasBow && !bow) {
        const s = itemSpriteMesh(env, 'bow');
        const holder = new THREE.Group();
        holder.add(s.mesh);
        applyHandheld(s.mesh, true);
        h.rightHand.add(holder);
        bow = holder; bowMat = s.mat;
        h.mats.push(s.mat);
      }
      if (bow) bow.visible = hasBow;
      void bowMat;
      h.animate({
        limbSwing: q.limbSwing, limbAmt: q.limbAmt, headY, headX, age,
        attack: v.attackAnim,
        zombieArms: kind === 'zombie' || (v.aggressive && !hasBow),
        bow: v.aggressive && hasBow,
        holding: hasBow && !v.aggressive,
        sneaking: false, swimming: false, bob: true,
      });
    },
    dispose() { for (const m of h.mats) m.dispose(); },
  };
}

function modelAnim(m: Model): Anim {
  return {
    parts: m.parts, mats: m.mats,
    // animal models keep their head part in `head`; babies draw it oversized
    head: (m as unknown as { head?: THREE.Object3D }).head,
    animate(v, q) { m.setView?.(v); m.animate(q); },
    dispose() { for (const x of m.mats) x.dispose(); },
  };
}

function makeAnim(env: EnvUniforms, t: MobType): Anim {
  switch (t) {
    case 'zombie': return humanoidAnim(env, 'zombie');
    case 'skeleton': return humanoidAnim(env, 'skeleton');
    case 'creeper': return modelAnim(new CreeperModel(env));
    case 'spider': return modelAnim(new SpiderModel(env));
    case 'pig': return modelAnim(new PigModel(env));
    case 'cow': return modelAnim(new CowModel(env));
    case 'sheep': return modelAnim(new SheepModel(env));
    case 'chicken': return modelAnim(new ChickenModel(env));
    case 'zombified_piglin': return piglinAnim(env, 'entity/piglin/zombified_piglin', true);
    case 'piglin': return piglinAnim(env, 'entity/piglin/piglin', false);
    case 'magma_cube': return magmaCubeAnim(env);
    case 'ghast': return ghastAnim(env);
    case 'blaze': return blazeAnim(env);
    case 'wither_skeleton': return witherSkeletonAnim(env);
    case 'hoglin': return hoglinAnim(env);
    case 'strider': return striderAnim(env);
    case 'enderman': return endermanAnim(env);
    case 'villager': return villagerAnim(env, false);
    case 'zombie_villager': return villagerAnim(env, true);
    case 'pillager': return illagerAnim(env, 'entity/illager/pillager', 'crossbow_standby', 0x8f8f8f);
    case 'vindicator': return illagerAnim(env, 'entity/illager/vindicator', 'iron_axe', 0x7d7d7d);
    case 'iron_golem': return ironGolemAnim(env);
    case 'slime': return slimeAnim(env);
    case 'cave_spider': return caveSpiderAnim(env);
    case 'husk': return huskAnim(env);
    case 'stray': return strayAnim(env);
    case 'drowned': return drownedAnim(env);
    case 'silverfish': return silverfishAnim(env);
    case 'endermite': return endermiteAnim(env);
    case 'wolf': return wolfAnim(env);
    case 'witch': return witchAnim(env);
    case 'phantom': return phantomAnim(env);
    case 'cat': return catAnim(env);
    case 'horse': case 'donkey': return horseAnim(env);
    case 'squid': return squidAnim(env);
    case 'cod': return fishAnim(env, 'cod');
    case 'salmon': return fishAnim(env, 'salmon');
    case 'bat': return batAnim(env);
    case 'wither': return witherAnim(env);
    case 'warden': return wardenAnim(env);
    case 'panda': return pandaAnim(env);
    case 'parrot': return parrotAnim(env);
    case 'fox': return foxAnim(env);
    case 'wandering_trader': return wanderingTraderAnim(env);
    case 'llama': return llamaAnim(env, false);
    case 'trader_llama': return llamaAnim(env, true);
    case 'rabbit': return rabbitAnim(env);
    case 'goat': return goatAnim(env);
    case 'turtle': return turtleAnim(env);
    case 'mooshroom': return mooshroomAnim(env);
    case 'polar_bear': return polarBearAnim(env);
    case 'ocelot': return ocelotAnim(env);
    case 'snow_golem': return snowGolemAnim(env);
    case 'axolotl': return axolotlAnim(env);
    case 'dolphin': return dolphinAnim(env);
    case 'guardian': return guardianAnim(env, false);
    case 'elder_guardian': return guardianAnim(env, true);
    case 'shulker': return shulkerAnim(env);
    case 'shulker_bullet': return shulkerBulletAnim(env);
    case 'evoker': return evokerAnim(env);
    case 'evoker_fangs': return evokerFangsAnim(env);
    case 'vex': return vexAnim(env);
    case 'ravager': return ravagerAnim(env);
    case 'zoglin': return hoglinAnim(env, 'entity/hoglin/zoglin');
    case 'piglin_brute': return piglinAnim(env, 'entity/piglin/piglin_brute', false, 'golden_axe');
  }
}

/**
 * Legacy scaled-adult babies (vanilla AgeableListModel babyYHeadOffset,
 * babyZHeadOffset in px): body scaled 1/2, head drawn full size. Every baby
 * in the game now has its own model (babyModels.ts), so this is empty; add a
 * type here only if it gains babies before it gains a baby model.
 */
const BABY_HEAD: Partial<Record<MobType, readonly [number, number]>> = {};

/** Put the head back at its rest pivot before the model animates it. */
function restoreHead(a: Anim): void {
  const h = a.head;
  if (!h) return;
  const base = (h.userData.base ??= h.position.clone()) as THREE.Vector3;
  h.position.copy(base);
}

/**
 * Baby transform in model px space (y down, feet at 24): parts are scaled by
 * 1/2 and shifted 12 px down (body = vanilla's 0.5 scale + 24/16 translate);
 * the head, a child of parts, is scaled back up 2x and its pivot placed where
 * vanilla draws it — full-size head at (pivot + baby offset) in unscaled space.
 */
function applyBaby(a: Anim, off: readonly [number, number], baby: boolean): void {
  const parts = a.parts, h = a.head;
  if (!baby) {
    parts.scale.setScalar(1); parts.position.set(0, 0, 0);
    h?.scale.setScalar(1);
    return;
  }
  parts.scale.setScalar(0.5); parts.position.set(0, 12, 0);
  if (!h) return;
  h.scale.setScalar(2);
  // world = 0.5 * local + (0, 12, 0)  =>  local = 2 * (want - (0, 12, 0))
  const p = h.position;
  p.set(2 * p.x, 2 * (p.y + off[0] - 12), 2 * (p.z + off[1]));
}

interface Inst {
  /** Pool key: the mob type, or `baby_<type>` for a baby with its own model. */
  type: string;
  babyModel: boolean;
  group: THREE.Group;
  death: THREE.Group;
  anim: Anim;
  fire: FireOverlay;
  plate?: THREE.Sprite;
  plateName?: string;
}

/** Name-tag plate: a camera-facing sprite just above the mob's head (vanilla: dark translucent backing, white text). */
function updatePlate(inst: Inst, name: string | undefined, height: number): void {
  if (!name) { if (inst.plate) inst.plate.visible = false; return; }
  if (!inst.plate || inst.plateName !== name) {
    if (inst.plate) { inst.group.remove(inst.plate); (inst.plate.material as THREE.SpriteMaterial).map?.dispose(); inst.plate.material.dispose(); }
    const c = document.createElement('canvas');
    const g0 = c.getContext('2d')!;
    g0.font = 'bold 28px sans-serif';
    const w = Math.ceil(g0.measureText(name).width) + 16;
    c.width = w; c.height = 40;
    const g = c.getContext('2d')!;
    g.fillStyle = 'rgba(0,0,0,0.25)'; g.fillRect(0, 0, w, 40);
    g.font = 'bold 28px sans-serif'; g.textBaseline = 'middle'; g.fillStyle = '#ffffff';
    g.fillText(name, 8, 21);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, fog: false }));
    spr.scale.set(w / 40 * 0.25, 0.25, 1);
    spr.renderOrder = 999;
    inst.group.add(spr);
    inst.plate = spr; inst.plateName = name;
  }
  inst.plate.visible = true;
  inst.plate.position.set(0, height + 0.5, 0);
}

interface ArrowInst { mesh: THREE.Mesh; mat: EntityMat }

export class EntityRenderer {
  readonly group = new THREE.Group();
  private pool = new Map<string, Inst[]>();
  private live = new Map<number, Inst>();
  private arrowPool: ArrowInst[] = [];
  private arrowLive = new Map<number, ArrowInst>();
  private viewer: THREE.Vector3 | null = null;
  private seen = new Set<number>();
  private seenArrows = new Set<number>();

  constructor(private readonly env: EnvUniforms) {
    this.group.frustumCulled = false;
  }

  /** Camera position, for distance culling (optional). */
  setViewer(x: number, y: number, z: number): void {
    (this.viewer ??= new THREE.Vector3()).set(x, y, z);
  }

  private acquire(t: MobType, key: string): Inst {
    let list = this.pool.get(key);
    if (!list) { list = []; this.pool.set(key, list); }
    const got = list.pop();
    if (got) { got.group.visible = true; return got; }
    const babyModel = key !== t;
    const anim = (babyModel ? babyAnim(this.env, t) : null) ?? makeAnim(this.env, t);
    const group = new THREE.Group();
    const death = new THREE.Group();
    const root = modelRoot();
    root.add(anim.parts);
    death.add(root);
    group.add(death);
    const [w, h] = SIZE[t];
    const fire = new FireOverlay(this.env, babyModel ? w / 2 : w, babyModel ? h / 2 : h);
    group.add(fire.group);
    this.group.add(group);
    return { type: key, babyModel, group, death, anim, fire };
  }

  private release(inst: Inst): void {
    inst.group.visible = false;
    this.pool.get(inst.type)!.push(inst);
  }

  update(mobs: readonly MobView[], arrows: readonly ArrowView[], alpha: number): void {
    const now = performance.now() / 1000;
    this.seen.clear();
    for (const v of mobs) {
      const x = v.px + (v.x - v.px) * alpha, y = v.py + (v.y - v.py) * alpha, z = v.pz + (v.z - v.pz) * alpha;
      if (this.viewer && v.deathTime < 20) {
        const dx = x - this.viewer.x, dz = z - this.viewer.z;
        if (dx * dx + dz * dz > CULL_DIST * CULL_DIST) continue;
      }
      if (v.deathTime >= 20) continue;
      this.seen.add(v.id);
      let inst = this.live.get(v.id);
      // babies with their own model are a separate pool from the adults
      const key = v.baby && hasBabyModel(v.type) ? `baby_${v.type}` : v.type;
      if (!inst || inst.type !== key) {
        if (inst) this.release(inst);
        inst = this.acquire(v.type, key);
        this.live.set(v.id, inst);
      }
      inst.group.visible = v.invisible !== true; // wandering trader under a Potion of Invisibility
      inst.group.position.set(x, y, z);
      inst.group.rotation.y = v.yaw;
      // death: tip over sideways about the feet (DeathAnimation, 90 deg over ~20 ticks)
      if (v.deathTime > 0) {
        const f = Math.min(1, Math.sqrt(Math.max(0, ((v.deathTime + alpha - 1) / 20) * 1.6)));
        inst.death.rotation.z = f * (PI / 2);
      } else inst.death.rotation.z = 0;
      // sleeping villager: lie on its back about the feet, head toward +yaw direction (Villager.sleepPose sets yaw)
      if (v.type === 'villager' && (v.color & 1024) !== 0 && v.deathTime === 0) {
        inst.death.rotation.x = PI / 2;
        inst.group.position.y = y + (v.baby ? 0.14 : 0.28);
      } else inst.death.rotation.x = 0;

      const age = now * 20 + v.id * 7.3;
      const q: QuadPose = {
        limbSwing: v.limbSwing, limbAmt: v.limbSwingAmount, headY: 0, headX: 0, age,
        swell: v.swell, airborne: v.y - v.py < -0.01, sheared: v.sheared, color: v.color,
        eat: v.eat ? Math.max(0, v.eat - alpha) : 0,
      };
      const headY = -wrapPi(v.headYaw - v.yaw);
      const headX = -v.pitch;
      q.headY = headY; q.headX = headX;
      const babyHead = inst.babyModel ? undefined : BABY_HEAD[v.type];
      if (babyHead) restoreHead(inst.anim);
      inst.anim.animate(v, q, headY, headX, age);
      if (babyHead) applyBaby(inst.anim, babyHead, v.baby === true);
      let flash = 0;
      if (v.type === 'creeper' && v.swell > 0) flash = Math.floor(v.swell * 10) % 2 === 0 ? 0 : Math.min(1, Math.max(0.5, v.swell));
      LIGHT_AT.x = v.x; LIGHT_AT.y = v.y + 0.5; LIGHT_AT.z = v.z;
      setEffects(inst.anim.mats, v.light, v.hurtTime > 0 || v.deathTime > 0, flash, LIGHT_AT);
      inst.fire.update(v.onFire, now);
      updatePlate(inst, v.name, SIZE[v.type][1] * (v.baby ? 0.5 : 1));
    }
    for (const [id, inst] of this.live) {
      if (!this.seen.has(id)) { this.release(inst); this.live.delete(id); }
    }

    this.seenArrows.clear();
    for (const a of arrows) {
      const x = a.px + (a.x - a.px) * alpha, y = a.py + (a.y - a.py) * alpha, z = a.pz + (a.z - a.pz) * alpha;
      this.seenArrows.add(a.id);
      let inst = this.arrowLive.get(a.id);
      if (!inst) {
        inst = this.arrowPool.pop();
        if (!inst) { inst = arrowMesh(this.env); this.group.add(inst.mesh); }
        inst.mesh.visible = true;
        this.arrowLive.set(a.id, inst);
      }
      inst.mesh.position.set(x, y, z);
      inst.mesh.rotation.set(0, a.yaw + PI / 2, a.pitch);
      inst.mat.uniforms.uLight.value = a.light;
    }
    for (const [id, inst] of this.arrowLive) {
      if (!this.seenArrows.has(id)) { inst.mesh.visible = false; this.arrowPool.push(inst); this.arrowLive.delete(id); }
    }
  }

  dispose(): void {
    for (const list of this.pool.values()) for (const i of list) { i.anim.dispose(); i.fire.dispose(); }
    for (const i of this.live.values()) { i.anim.dispose(); i.fire.dispose(); }
    for (const a of [...this.arrowPool, ...this.arrowLive.values()]) a.mat.dispose();
    this.group.clear();
    this.pool.clear(); this.live.clear(); this.arrowPool.length = 0; this.arrowLive.clear();
  }
}
